import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  InterWarehouseTransfer,
  InterWarehouseTransferLine,
  InterWarehouseTransferStatus,
  TransferAvailableLot,
  Warehouse as WarehouseType,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import MesPagination, { usePagedList } from "../../../components/MES/MesPagination";
import { t, dateLocale } from "../../../i18n/locale";

type ViewMode = "planner" | "warehouse";

const TRANSFERS_PAGE_SIZE = 12;
const PENDING_CARDS_PAGE_SIZE = 8;

const STATUS_LABELS: Record<InterWarehouseTransferStatus, string> = {
  draft: "Nacrt",
  ready: "Čeka prijenos",
  in_transit: "U prijenosu",
  completed: "Preneseno",
  cancelled: "Otkazano",
};

const STATUS_CLASS: Record<InterWarehouseTransferStatus, string> = {
  draft: "badge-draft",
  ready: "badge-quarantine",
  in_transit: "badge-quarantine",
  completed: "stock-good",
  cancelled: "stock-empty",
};

const todayIso = () => new Date().toISOString().split("T")[0];

const emptyManualLine = () => ({
  material_lot_id: 0,
  material_name: "",
  lot_number: "",
  remaining: 0,
  quantity_kg: "",
});

/** FEFO pick (kg na LOT-u) ili potreba iz plana kad nema skladišnog LOT-a */
const isActionableLine = (s: InterWarehouseTransferLine) =>
  s.quantity_kg > 0.001 || (s.required_quantity_kg != null && s.required_quantity_kg > 0.001);

const isShortageLine = (s: InterWarehouseTransferLine) =>
  isActionableLine(s) && s.quantity_kg <= 0.001;

const transferProgress = (lines: InterWarehouseTransferLine[]) => {
  const required = (lines || []).filter(isActionableLine);
  const done = required.filter((s) => s.transferred).length;
  return { done, total: required.length, allDone: required.length > 0 && done === required.length };
};

const InterWarehouseTransfers = () => {
  savePathToLocalStorage();

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem("mes_iwt_view");
    return saved === "planner" ? "planner" : "warehouse";
  });

  const [transfers, setTransfers] = useState<InterWarehouseTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDate, setFilterDate] = useState(todayIso());
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<InterWarehouseTransfer | null>(null);

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [genDate, setGenDate] = useState(todayIso());
  const [preview, setPreview] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [manualDate, setManualDate] = useState(todayIso());
  const [manualNote, setManualNote] = useState("");
  const [manualLines, setManualLines] = useState([emptyManualLine()]);
  const [availableLots, setAvailableLots] = useState<TransferAvailableLot[]>([]);
  const [togglingLineId, setTogglingLineId] = useState<number | null>(null);

  const [warehouses, setWarehouses] = useState<WarehouseType[]>([]);
  const [manualFromWarehouseId, setManualFromWarehouseId] = useState(0);
  const [manualToWarehouseId, setManualToWarehouseId] = useState(0);

  const warehousePending = useMemo(
    () => transfers.filter((t) => t.status === "ready"),
    [transfers]
  );
  const transfersPager = usePagedList(transfers, TRANSFERS_PAGE_SIZE);
  const pendingPager = usePagedList(warehousePending, PENDING_CARDS_PAGE_SIZE);
  const { setPage: setTransfersPage } = transfersPager;
  const { setPage: setPendingPage } = pendingPager;

  const manualFromWarehouseCode = warehouses.find(
    (w) => w.warehouse_id === manualFromWarehouseId
  )?.code;

  useEffect(() => {
    mesApi
      .getSkladista()
      .then((list) => {
        setWarehouses(list);
        setManualFromWarehouseId(
          (prev) => prev || list.find((w) => w.is_default_transfer_source)?.warehouse_id || 0
        );
        setManualToWarehouseId(
          (prev) => prev || list.find((w) => w.is_default_transfer_target)?.warehouse_id || 0
        );
      })
      .catch(() => toast.error(t("Popis skladišta nije učitan")));
  }, []);

  // Lotovi se biraju iz zalihe početnog skladišta, pa se popis ponovno dohvaća
  // kad se ono promijeni — inače bi se ponudio lot koji tamo ne stoji.
  useEffect(() => {
    if (!showManualModal || !manualFromWarehouseCode) {
      setAvailableLots([]);
      return;
    }
    let cancelled = false;
    mesApi
      .getTransferAvailableLots(manualFromWarehouseCode)
      .then((lots) => {
        if (!cancelled) setAvailableLots(lots);
      })
      .catch(() => {
        if (!cancelled) setAvailableLots([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showManualModal, manualFromWarehouseCode]);

  useEffect(() => {
    localStorage.setItem("mes_iwt_view", viewMode);
    if (viewMode === "warehouse") {
      // Show all pending transfers, not just today's — a transfer's production date
      // is often a different day than when the warehouse worker processes it.
      setFilterDate("");
      setFilterStatus("ready");
    } else {
      setFilterStatus("");
    }
    setSelectedId(null);
  }, [viewMode]);

  useEffect(() => {
    loadTransfers();
    // Nova pretraga počinje od prve stranice; osvježavanja nakon označavanja
    // stavke namjerno ostavljaju skladištara na stranici na kojoj je bio.
    setTransfersPage(1);
    setPendingPage(1);
  }, [filterStatus, filterDate, viewMode, setTransfersPage, setPendingPage]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId]);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const filters: { status?: string; from_date?: string; to_date?: string } = {};
      if (filterStatus) filters.status = filterStatus;
      if (filterDate) {
        filters.from_date = filterDate;
        filters.to_date = filterDate;
      }
      const data = await mesApi.getInterWarehouseTransfers(filters);
      setTransfers(data || []);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id: number) => {
    try {
      const data = await mesApi.getInterWarehouseTransfer(id);
      setDetail(data);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      setPreview(await mesApi.previewInterWarehouseTransfer(genDate));
    } catch (err: any) {
      toast.error(err.message);
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const data = await mesApi.generateInterWarehouseTransfer(genDate, preview?.timeline_id);
      toast.success(t("Objavljeno skladištaru: {number}", { number: data.transfer_number }));
      setShowGenerateModal(false);
      setPreview(null);
      setSelectedId(data.transfer_id);
      loadTransfers();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openManualModal = () => {
    setManualDate(filterDate || todayIso());
    setManualNote("");
    setManualLines([emptyManualLine()]);
    setShowManualModal(true);
  };

  /** Lot pripada skladištu s kojeg se prenosi, pa odabrane stavke padaju s njim. */
  const handleChangeManualSource = (warehouseId: number) => {
    setManualFromWarehouseId(warehouseId);
    setManualLines([emptyManualLine()]);
  };

  const handleManualCreate = async (asDraft: boolean) => {
    if (!manualFromWarehouseId || !manualToWarehouseId) {
      toast.error(t("Odaberite početno i odredišno skladište"));
      return;
    }
    if (manualFromWarehouseId === manualToWarehouseId) {
      toast.error(t("Početno i odredišno skladište moraju biti različiti"));
      return;
    }

    const lines = manualLines
      .filter((l) => l.material_lot_id && parseFloat(l.quantity_kg) > 0)
      .map((l) => ({
        material_lot_id: l.material_lot_id,
        lot_number: l.lot_number || undefined,
        quantity_kg: parseFloat(l.quantity_kg),
      }));

    if (!lines.length) {
      toast.error(t("Odaberite lot i upišite količinu za barem jednu stavku"));
      return;
    }

    const overStock = manualLines.find(
      (l) => l.material_lot_id && parseFloat(l.quantity_kg) > l.remaining + 0.001
    );
    if (overStock) {
      toast.error(t("Količina prelazi zalihu lota ({name}: {remaining})", { name: overStock.material_name, remaining: overStock.remaining }));
      return;
    }

    try {
      setLoading(true);
      const data = await mesApi.createInterWarehouseTransfer({
        production_date: manualDate,
        from_warehouse_id: manualFromWarehouseId,
        to_warehouse_id: manualToWarehouseId,
        notes: manualNote || undefined,
        publish: !asDraft,
        created_by: viewMode === "warehouse" ? "skladistar" : undefined,
        lines,
      });
      toast.success(asDraft ? t("Nacrt {number}", { number: data.transfer_number }) : t("Kreirano: {number}", { number: data.transfer_number }));
      setShowManualModal(false);
      setManualLines([emptyManualLine()]);
      setSelectedId(data.transfer_id);
      setDetail(data);
      loadTransfers();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLine = async (lineId: number, transferred: boolean) => {
    if (!detail) return;
    try {
      setTogglingLineId(lineId);
      const updated = await mesApi.setInterWarehouseTransferLineTransferred(
        detail.transfer_id,
        lineId,
        transferred
      );
      setDetail(updated);
      loadTransfers();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setTogglingLineId(null);
    }
  };

  const handlePublish = async (id: number) => {
    try {
      setLoading(true);
      await mesApi.publishInterWarehouseTransfer(id);
      toast.success(t("Objavljeno skladištaru"));
      loadTransfers();
      loadDetail(id);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm(t("Otkazati međuskladišnicu?"))) return;
    try {
      setLoading(true);
      await mesApi.updateInterWarehouseTransferStatus(id, "cancelled");
      toast.success(t("Otkazano"));
      loadTransfers();
      if (selectedId === id) loadDetail(id);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (id: number) => {
    const progress = detail ? transferProgress(detail.lines || []) : { allDone: false };
    if (!progress.allDone) {
      toast.error(t("Označite sve stavke prije završetka"));
      return;
    }
    if (!window.confirm(t("Završiti međuskladišnicu i knjižiti prenesene količine?"))) return;
    try {
      setLoading(true);
      await mesApi.completeInterWarehouseTransfer(id, "skladistar");
      toast.success(t("Označeno kao preneseno"));
      loadTransfers();
      setSelectedId(null);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d?: string) => (d ? new Date(d).toLocaleDateString(dateLocale()) : "-");

  const detailProgress = detail ? transferProgress(detail.lines || []) : null;

  const renderStavkeTable = (
    transfer: InterWarehouseTransfer,
    options: { allowCheck: boolean }
  ) => {
    const lines = transfer.lines || [];
    const progress = transferProgress(lines);
    const showCheckColumn = options.allowCheck || transfer.status === "ready";

    return (
      <>
        {showCheckColumn && (
          <p className="iwt-progress">
            {t("Preneseno:")} <strong>{progress.done}</strong> / {progress.total} {t("stavki")}
            {progress.total === 0 && ` ${t("(nema stavki za prijenos)")}`}
          </p>
        )}
        <div className="iwt-table-scroll">
        <table className="mes-table iwt-lines-table">
          <thead>
            <tr>
              {showCheckColumn && <th style={{ width: 48 }}>✓</th>}
              <th>{t("Sirovina")}</th>
              <th>{t("LOT")}</th>
              <th>{t("Rok")}</th>
              <th style={{ textAlign: "right" }}>{t("Prenijeti")}</th>
              <th style={{ textAlign: "right" }}>{t("Potrebno")}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((s) => {
              const actionable = isActionableLine(s);
              const rowClass = !actionable
                ? "iwt-line-warn"
                : isShortageLine(s)
                  ? "iwt-line-shortage"
                  : s.transferred
                    ? "iwt-line-done"
                    : "";
              return (
                <tr key={s.line_id} className={rowClass}>
                  {showCheckColumn && (
                    <td>
                      <div className="iwt-check-cell">
                      {actionable ? (
                        options.allowCheck && s.line_id ? (
                          <input
                            type="checkbox"
                            checked={!!s.transferred}
                            disabled={togglingLineId === s.line_id}
                            onChange={(e) =>
                              handleToggleLine(s.line_id, e.target.checked)
                            }
                            title={t("Označite kad prenesete u proizvodnju")}
                          />
                        ) : options.allowCheck ? (
                          <span className="iwt-na-check" title={t("Stavka nema ID")}>—</span>
                        ) : (
                          <span className={s.transferred ? "iwt-check-done" : "iwt-check-pending"}>
                            {s.transferred ? "✓" : "○"}
                          </span>
                        )
                      ) : (
                        <span className="iwt-na-check" title={t("Nema potrebe za prijenosom")}>—</span>
                      )}
                      </div>
                    </td>
                  )}
                  <td>
                    {s.ingredient_name}
                    {isShortageLine(s) && s.notes && (
                      <span className="iwt-line-note">{s.notes}</span>
                    )}
                  </td>
                  <td>
                    <strong>{s.lot_number || "—"}</strong>
                  </td>
                  <td>{formatDate(s.expiry_date)}</td>
                  <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                    {s.quantity_kg > 0 ? s.quantity_kg.toFixed(2) : "—"}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "monospace", opacity: 0.9 }}>
                    {s.required_quantity_kg != null && s.required_quantity_kg > 0
                      ? s.required_quantity_kg.toFixed(2)
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {options.allowCheck && (
          <p className="iwt-fefo-hint">
            {t("Označite svaku prenesenu stavku (✓). Žuti redovi: nema LOT-a u sustavu — prenesite ručno prema \"Potrebno\" kg.")}
          </p>
        )}
      </>
    );
  };

  return (
    <div className="mes-page">
      <Header title={t("MES - Skladiste")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2500} />

          <div className="iwt-header">
            <div>
              <h2>{t("Međuskladišnice")}</h2>
              <p className="iwt-role-hint">
                {viewMode === "planner"
                  ? t("Voditelj proizvodnje — kreiraj i objavi listu za skladište")
                  : t("Skladištar — pregled i potvrda prijenosa za odabrani dan")}
              </p>
            </div>
            <div className="iwt-view-switch">
              <button
                type="button"
                className={viewMode === "warehouse" ? "btn-primary" : "btn-secondary"}
                onClick={() => setViewMode("warehouse")}
              >
                {t("Skladištar")}
              </button>
              <button
                type="button"
                className={viewMode === "planner" ? "btn-primary" : "btn-secondary"}
                onClick={() => setViewMode("planner")}
              >
                {t("Voditelj")}
              </button>
            </div>
          </div>

          {viewMode === "warehouse" ? (
            <div className="iwt-warehouse-view">
              <div className="iwt-warehouse-toolbar">
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t("Dan")}</label>
                  <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                  />
                </div>
                <span className="iwt-pending-badge">
                  {t("{count} za prijenos", { count: warehousePending.length })}
                </span>
                <button type="button" className="btn-primary" onClick={openManualModal}>
                  {t("+ Nova međuskladišnica")}
                </button>
              </div>

              {loading ? (
                <p className="iwt-empty">{t("Učitavanje...")}</p>
              ) : warehousePending.length === 0 ? (
                <p className="iwt-empty">
                  {filterDate
                    ? t("Nema međuskladišnica koje čekaju prijenos za {date}", { date: formatDate(filterDate) })
                    : t("Nema međuskladišnica koje čekaju prijenos")}
                </p>
              ) : (
                <>
                <div className="iwt-warehouse-cards">
                  {pendingPager.pageItems.map((xfer) => (
                    <div key={xfer.transfer_id} className="iwt-warehouse-card">
                      <div className="iwt-warehouse-card-head">
                        <div>
                          <strong>{xfer.transfer_number}</strong>
                          <span className="iwt-route">
                            {xfer.from_warehouse_name || xfer.from_location} →{" "}
                            {xfer.to_warehouse_name || xfer.to_location}
                          </span>
                        </div>
                        <span className="badge badge-quarantine">{t("Za prijenos")}</span>
                      </div>
                      <p className="iwt-card-meta">
                        {t("{count} stavki", { count: xfer.lines_count ?? 0 })}
                        {xfer.timeline && ` · ${t("Plan v{version}", { version: xfer.timeline.version })}`}
                      </p>
                      <button
                        type="button"
                        className="btn-save iwt-complete-btn"
                        disabled={loading}
                        onClick={() => {
                          setSelectedId(xfer.transfer_id);
                          loadDetail(xfer.transfer_id);
                        }}
                      >
                        {t("Prikaži stavke")}
                      </button>
                    </div>
                  ))}
                </div>
                <MesPagination pager={pendingPager} itemLabel={t("za prijenos")} />
                </>
              )}

              {detail && detail.status === "ready" && (
                <div className="mes-section iwt-warehouse-detail">
                  <h3>
                    {detail.transfer_number} — {detail.from_warehouse_name || detail.from_location} →{" "}
                    {detail.to_warehouse_name || detail.to_location}
                  </h3>
                  {detail.notes && <p className="iwt-note">{detail.notes}</p>}
                  {renderStavkeTable(detail, { allowCheck: true })}
                  <button
                    type="button"
                    className="btn-save iwt-complete-btn"
                    disabled={loading || !detailProgress?.allDone}
                    onClick={() => handleComplete(detail.transfer_id)}
                  >
                    {t("Završi međuskladišnicu")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="iwt-header-actions" style={{ marginBottom: 16 }}>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="filter-select"
                />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="filter-select"
                >
                  <option value="">{t("Svi statusi")}</option>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {t(v)}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn-secondary" onClick={openManualModal}>
                  {t("+ Ručna")}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    setGenDate(filterDate || todayIso());
                    setShowGenerateModal(true);
                    setPreview(null);
                  }}
                >
                  {t("+ Iz plana")}
                </button>
              </div>

              <div className="iwt-layout">
                <div className="mes-section iwt-list-panel">
                  <h3>{t("Pregled")}</h3>
                  {loading && transfers.length === 0 ? (
                    <p className="iwt-empty">{t("Učitavanje...")}</p>
                  ) : transfers.length === 0 ? (
                    <p className="iwt-empty">{t("Nema međuskladišnica")}</p>
                  ) : (
                    <>
                    <div className="mes-table-wrap">
                      <table className="mes-table">
                      <thead>
                        <tr>
                          <th>{t("Broj")}</th>
                          <th>{t("Datum")}</th>
                          <th>{t("Ruta")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Stavki")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transfersPager.pageItems.map((xfer) => (
                          <tr
                            key={xfer.transfer_id}
                            className={selectedId === xfer.transfer_id ? "row-selected" : ""}
                            onClick={() => setSelectedId(xfer.transfer_id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              <strong>{xfer.transfer_number}</strong>
                            </td>
                            <td>{formatDate(xfer.production_date)}</td>
                            <td className="iwt-route-cell">
                              {xfer.from_warehouse_name || xfer.from_location || "—"} →{" "}
                              {xfer.to_warehouse_name || xfer.to_location || "—"}
                            </td>
                            <td>
                              <span className={`badge ${STATUS_CLASS[xfer.status]}`}>
                                {t(STATUS_LABELS[xfer.status])}
                              </span>
                            </td>
                            <td>{xfer.lines_count ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    <MesPagination pager={transfersPager} itemLabel={t("dokumenata")} />
                    </>
                  )}
                </div>

                <div className="mes-section iwt-detail-panel">
                  <h3>{t("Detalji")}</h3>
                  {!detail ? (
                    <p className="iwt-empty">{t("Odaberite dokument")}</p>
                  ) : (
                    <>
                      <div className="iwt-detail-meta">
                        <div>
                          <strong>{detail.transfer_number}</strong>
                          <span className={`badge ${STATUS_CLASS[detail.status]}`}>
                            {t(STATUS_LABELS[detail.status])}
                          </span>
                        </div>
                        <p>
                          {detail.from_warehouse_name || detail.from_location || "—"} →{" "}
                          {detail.to_warehouse_name || detail.to_location || "—"} ·{" "}
                          {formatDate(detail.production_date)}
                        </p>
                      </div>

                      <div className="iwt-detail-actions">
                        {detail.status === "draft" && (
                          <>
                            <button
                              type="button"
                              className="btn-save"
                              disabled={loading}
                              onClick={() => handlePublish(detail.transfer_id)}
                            >
                              {t("Objavi skladištaru")}
                            </button>
                            <button
                              type="button"
                              className="btn-cancel"
                              disabled={loading}
                              onClick={() => handleCancel(detail.transfer_id)}
                            >
                              {t("Otkaži")}
                            </button>
                          </>
                        )}
                        {detail.status === "ready" && (
                          <button
                            type="button"
                            className="btn-cancel"
                            disabled={loading}
                            onClick={() => handleCancel(detail.transfer_id)}
                          >
                            {t("Otkaži")}
                          </button>
                        )}
                      </div>

                      {detail.status === "ready" ? (
                        renderStavkeTable(detail, { allowCheck: true })
                      ) : (
                        <div className="mes-table-wrap">
                          <table className="mes-table iwt-lines-table">
                          <thead>
                            <tr>
                              <th>{t("Sirovina")}</th>
                              <th>{t("LOT")}</th>
                              <th>{t("Rok")}</th>
                              <th style={{ textAlign: "right" }}>{t("Kg")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.lines || []).map((s) => (
                              <tr key={s.line_id}>
                                <td>{s.ingredient_name}</td>
                                <td>
                                  <strong>{s.lot_number || "—"}</strong>
                                </td>
                                <td>{formatDate(s.expiry_date)}</td>
                                <td style={{ textAlign: "right" }}>
                                  {s.quantity_kg > 0 ? s.quantity_kg.toFixed(2) : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      )}
                      {detail.status === "ready" && detailProgress && (
                        <>
                          <p className="iwt-progress">
                            {t("Skladištar: {done}/{total} preneseno", { done: detailProgress.done, total: detailProgress.total })}
                          </p>
                          <button
                            type="button"
                            className="btn-save iwt-complete-btn"
                            disabled={loading || !detailProgress.allDone}
                            onClick={() => handleComplete(detail.transfer_id)}
                          >
                            {t("Završi međuskladišnicu")}
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showGenerateModal && (
        <div className="mes-modal-overlay" onClick={() => setShowGenerateModal(false)}>
          <div className="mes-modal mes-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t("Iz plana proizvodnje")}</h3>
              <button type="button" className="close-btn" onClick={() => setShowGenerateModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t("Datum proizvodnje")}</label>
                <input type="date" value={genDate} onChange={(e) => { setGenDate(e.target.value); setPreview(null); }} />
              </div>
              <button type="button" className="btn-secondary" onClick={handlePreview} disabled={previewLoading}>
                {previewLoading ? "..." : t("Pregled (FEFO)")}
              </button>
              {preview && (
                <div className="iwt-preview">
                  <p>
                    {t("{batches} zamjesa · {ingredients} sirovina", { batches: preview.batch_count, ingredients: preview.demands?.length ?? 0 })}
                  </p>
                  <div className="mes-table-wrap">
                    <table className="mes-table">
                    <thead>
                      <tr>
                        <th>{t("Sirovina")}</th>
                        <th>{t("LOT")}</th>
                        <th>{t("Rok")}</th>
                        <th style={{ textAlign: "right" }}>{t("Kg")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.lines || []).map((l: any, i: number) => (
                        <tr key={i}>
                          <td>{l.ingredient_name}</td>
                          <td>{l.lot_number || "—"}</td>
                          <td>{l.expiry_date ? formatDate(l.expiry_date) : "—"}</td>
                          <td style={{ textAlign: "right" }}>{l.quantity_kg?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowGenerateModal(false)}>
                {t("Odustani")}
              </button>
              <button type="button" className="btn-save" disabled={!preview || loading} onClick={handleGenerate}>
                {t("Objavi skladištaru")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showManualModal && (
        <div className="mes-modal-overlay" onClick={() => setShowManualModal(false)}>
          <div className="mes-modal mes-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t("Ručna međuskladišnica")}</h3>
              <button type="button" className="close-btn" onClick={() => setShowManualModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid-2">
                <div className="form-group">
                  <label>{t("Iz skladišta *")}</label>
                  <select
                    value={manualFromWarehouseId || ""}
                    onChange={(e) => handleChangeManualSource(Number(e.target.value))}
                  >
                    <option value="">{t("-- Odaberi skladište --")}</option>
                    {warehouses.map((w) => (
                      <option key={w.warehouse_id} value={w.warehouse_id}>
                        {w.code} · {w.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t("U skladište *")}</label>
                  <select
                    value={manualToWarehouseId || ""}
                    onChange={(e) => setManualToWarehouseId(Number(e.target.value))}
                  >
                    <option value="">{t("-- Odaberi skladište --")}</option>
                    {warehouses
                      .filter((w) => w.warehouse_id !== manualFromWarehouseId)
                      .map((w) => (
                        <option key={w.warehouse_id} value={w.warehouse_id}>
                          {w.code} · {w.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>{t("Datum")}</label>
                  <input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t("Napomena")}</label>
                  <input type="text" value={manualNote} onChange={(e) => setManualNote(e.target.value)} />
                </div>
              </div>
              <div className="delivery-lines-header">
                <h4>{t("Stavke")}</h4>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={!manualFromWarehouseId}
                  onClick={() => setManualLines((p) => [...p, emptyManualLine()])}
                >
                  {t("+ Stavka")}
                </button>
              </div>
              {!manualFromWarehouseId ? (
                <p className="iwt-empty">{t("Odaberite početno skladište da vidite dostupne lotove")}</p>
              ) : availableLots.length === 0 ? (
                <p className="iwt-empty">{t("Odabrano skladište nema zaliha za prijenos")}</p>
              ) : null}
              {manualLines.map((line, idx) => (
                <div key={idx} className="form-grid-2 iwt-manual-line">
                  <select
                    disabled={!manualFromWarehouseId}
                    value={line.material_lot_id}
                    onChange={(e) => {
                      const lotId = parseInt(e.target.value, 10);
                      const lot = availableLots.find((l) => l.material_lot_id === lotId);
                      const next = [...manualLines];
                      next[idx] = {
                        ...next[idx],
                        material_lot_id: lotId,
                        material_name: lot?.material_name || "",
                        lot_number: lot?.lot_number || "",
                        remaining: lot?.remaining || 0,
                      };
                      setManualLines(next);
                    }}
                  >
                    <option value={0}>{t("Materijal / LOT")}</option>
                    {availableLots.map((l) => (
                      <option key={l.material_lot_id} value={l.material_lot_id}>
                        {t("{name} — {lot} (stanje {remaining})", {
                          name: l.material_name,
                          lot: l.lot_number || `#${l.material_lot_id}`,
                          remaining: l.remaining,
                        })}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={line.remaining ? t("Kg (max {remaining})", { remaining: line.remaining }) : t("Kg")}
                    value={line.quantity_kg}
                    onChange={(e) => {
                      const next = [...manualLines];
                      next[idx].quantity_kg = e.target.value;
                      setManualLines(next);
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowManualModal(false)}>
                {t("Odustani")}
              </button>
              {viewMode === "planner" && (
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={loading}
                    onClick={() => handleManualCreate(true)}
                  >
                    {t("Spremi nacrt")}
                  </button>
                  <button type="button" className="btn-save" disabled={loading} onClick={() => handleManualCreate(false)}>
                    {t("Objavi skladištaru")}
                  </button>
                </>
              )}
              {viewMode === "warehouse" && (
                <button type="button" className="btn-save" disabled={loading} onClick={() => handleManualCreate(false)}>
                  {t("Kreiraj i otvori listu")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterWarehouseTransfers;
